import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { io } from 'socket.io-client';
import axios from 'axios';

// In production nginx proxies /api and /socket.io on same origin
const API_BASE = process.env.REACT_APP_API_URL ||
  (window.location.hostname === 'localhost' ? 'http://localhost:5000' : '');

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}
function getColor(name) {
  const c = ['#f59e0b','#10b981','#3b82f6','#8b5cf6','#ef4444','#06b6d4','#ec4899','#84cc16','#f97316'];
  let h = 0; for (const ch of (name || '')) h += ch.charCodeAt(0);
  return c[h % c.length];
}
function fmtTime(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
function fmtDate(ts) {
  if (!ts) return '';
  const d = new Date(ts), t = new Date();
  if (d.toDateString() === t.toDateString()) return 'Today';
  const y = new Date(t); y.setDate(t.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return 'Yesterday';
  return d.toLocaleDateString();
}

// Short beep via Web Audio API — no external file needed
function playPing() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.08);
    gain.gain.setValueAtTime(0.25, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.35);
  } catch { /* browser blocked audio — silent fallback */ }
}

function showBrowserNotif(title, body, icon, onClick) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  const n = new Notification(title, { body, icon: icon || '/favicon.ico', tag: title });
  n.onclick = () => { window.focus(); onClick?.(); n.close(); };
}

export default function StaffChat() {
  const [user, setUser] = useState(() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } });
  const [token, setToken] = useState(() => localStorage.getItem('token'));

  const [open, setOpen] = useState(false);
  const [activeConv, setActiveConv] = useState('group'); // 'group' | email string

  // messages[conv] = array of message objects
  const [messages, setMessages] = useState({ group: [] });
  // unread[conv] = number
  const [unread, setUnread] = useState({ group: 0 });
  const [totalUnread, setTotalUnread] = useState(0);

  const [onlineUsers, setOnlineUsers] = useState([]);
  const [allStaff, setAllStaff] = useState([]);

  const [text, setText] = useState('');
  const [mentionQuery, setMentionQuery] = useState(null); // null | string
  const [mentionIndex, setMentionIndex] = useState(0);
  const [typingLabel, setTypingLabel] = useState('');

  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [btnPing, setBtnPing] = useState(false); // triggers ring animation on new message

  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);
  const mediaRecRef = useRef(null);
  const audioChunks = useRef([]);
  const typingTimer = useRef(null);
  const typingClear = useRef(null);
  // Refs to give socket handlers live access to open/activeConv without stale closure
  const openRef = useRef(open);
  const activeConvRef = useRef(activeConv);
  const baseTitleRef = useRef(document.title); // for tab-title badge

  useEffect(() => { openRef.current = open; }, [open]);
  useEffect(() => { activeConvRef.current = activeConv; }, [activeConv]);

  // Request browser notification permission once on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Update browser tab title with unread count
  useEffect(() => {
    if (totalUnread > 0) {
      document.title = `(${totalUnread}) ${baseTitleRef.current}`;
    } else {
      document.title = baseTitleRef.current;
    }
    return () => { document.title = baseTitleRef.current; };
  }, [totalUnread]);

  // Sync auth from localStorage events
  useEffect(() => {
    const sync = () => {
      try { setUser(JSON.parse(localStorage.getItem('user'))); } catch { setUser(null); }
      setToken(localStorage.getItem('token'));
    };
    window.addEventListener('storage', sync);
    window.addEventListener('user-login', sync);
    return () => { window.removeEventListener('storage', sync); window.removeEventListener('user-login', sync); };
  }, []);

  // Socket connection
  useEffect(() => {
    if (!token) return;
    const socket = io(API_BASE, {
      auth: { token },
      transports: ['polling', 'websocket'], // polling first — works through nginx without WS config
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      // Fetch group history on connect
      fetchHistory('group');
    });

    socket.on('online_users', users => setOnlineUsers(users));

    socket.on('new_message', msg => {
      setMessages(prev => ({ ...prev, group: [...(prev.group || []), msg] }));
      const myEmail = JSON.parse(localStorage.getItem('user') || '{}').email;
      const isActive = openRef.current && activeConvRef.current === 'group';
      setUnread(prev => {
        const newCount = isActive ? 0 : (prev.group || 0) + 1;
        return { ...prev, group: newCount };
      });
      if (msg.sender_id !== myEmail && !isActive) {
        playPing();
        setBtnPing(true);
        showBrowserNotif(
          'Group Chat',
          `${msg.sender_name}: ${msg.content || '📎 File'}`,
          '/favicon.ico',
          () => { setOpen(true); setActiveConv('group'); }
        );
      }
    });

    socket.on('new_direct_message', msg => {
      const myEmail = JSON.parse(localStorage.getItem('user') || '{}').email;
      const conv = msg.sender_id === myEmail ? msg.recipient_id : msg.sender_id;
      setMessages(prev => ({ ...prev, [conv]: [...(prev[conv] || []), msg] }));
      const isActive = openRef.current && activeConvRef.current === conv;
      setUnread(prev => {
        const newCount = isActive ? 0 : (prev[conv] || 0) + 1;
        return { ...prev, [conv]: newCount };
      });
      if (msg.sender_id !== myEmail && !isActive) {
        playPing();
        setBtnPing(true);
        showBrowserNotif(
          msg.sender_name || 'Staff',
          msg.content || '📎 File',
          '/favicon.ico',
          () => { setOpen(true); setActiveConv(conv); }
        );
      }
    });

    socket.on('user_typing', ({ name, to, from }) => {
      const myEmail = JSON.parse(localStorage.getItem('user') || '{}').email;
      // Show typing in group if to=null, or in DM if to=myEmail
      if (to === null) setTypingLabel(`${name} is typing...`);
      else if (to === myEmail) setTypingLabel(`${name} is typing...`);
      clearTimeout(typingClear.current);
      typingClear.current = setTimeout(() => setTypingLabel(''), 2000);
    });

    socket.on('user_stop_typing', () => setTypingLabel(''));

    socket.on('connect_error', (err) => console.warn('Chat socket error:', err.message));

    return () => socket.disconnect();
  }, [token]); // eslint-disable-line

  // Fetch all staff for sidebar
  useEffect(() => {
    if (!token) return;
    axios.get(`${API_BASE}/api/chat/staff-list`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setAllStaff(r.data || []))
      .catch(() => {});
  }, [token]);

  // Fetch history when switching conversations
  const fetchHistory = useCallback(async (conv) => {
    if (!token) return;
    try {
      const url = conv === 'group'
        ? `${API_BASE}/api/chat/history`
        : `${API_BASE}/api/chat/history?with=${encodeURIComponent(conv)}`;
      const { data } = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setMessages(prev => ({ ...prev, [conv]: data || [] }));
    } catch { /* ignore */ }
  }, [token]);

  const switchConv = (conv) => {
    setActiveConv(conv);
    setUnread(prev => ({ ...prev, [conv]: 0 }));
    fetchHistory(conv);
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const handleClearChat = () => {
    const conv = activeConv;
    setMessages(prev => ({ ...prev, [conv]: [] }));
    const url = conv === 'group'
      ? `${API_BASE}/api/chat/history`
      : `${API_BASE}/api/chat/history?with=${encodeURIComponent(conv)}`;
    axios.delete(url, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
  };

  // Auto-scroll on new messages
  useEffect(() => {
    if (open) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open, activeConv]);

  // Total unread badge on button
  useEffect(() => {
    setTotalUnread(Object.values(unread).reduce((a, b) => a + b, 0));
  }, [unread]);

  // Auto-clear the ring animation after it fires (1.2 s = 2 ping cycles)
  useEffect(() => {
    if (!btnPing) return;
    const t = setTimeout(() => setBtnPing(false), 1200);
    return () => clearTimeout(t);
  }, [btnPing]);

  // When opening, clear active conv unread
  useEffect(() => {
    if (open) setUnread(prev => ({ ...prev, [activeConv]: 0 }));
  }, [open]); // eslint-disable-line

  // ── Mention logic ──
  const mentionSuggestions = mentionQuery !== null
    ? allStaff.filter(s => s.name.toLowerCase().includes(mentionQuery.toLowerCase()) && s.email !== user?.email).slice(0, 6)
    : [];

  const insertMention = (name) => {
    const val = text;
    const atIdx = val.lastIndexOf('@');
    const newText = val.slice(0, atIdx) + '@' + name + ' ';
    setText(newText);
    setMentionQuery(null);
    inputRef.current?.focus();
  };

  const handleTyping = (e) => {
    const val = e.target.value;
    setText(val);

    // @mention detection
    const cur = e.target.selectionStart;
    const before = val.slice(0, cur);
    const atIdx = before.lastIndexOf('@');
    if (atIdx !== -1 && !before.slice(atIdx).includes(' ')) {
      setMentionQuery(before.slice(atIdx + 1));
      setMentionIndex(0);
    } else {
      setMentionQuery(null);
    }

    // Typing indicator
    if (socketRef.current) {
      socketRef.current.emit('typing', activeConv === 'group' ? {} : { to: activeConv });
      clearTimeout(typingTimer.current);
      typingTimer.current = setTimeout(() => {
        socketRef.current?.emit('stop_typing', activeConv === 'group' ? {} : { to: activeConv });
      }, 1500);
    }
  };

  const handleKeyDown = (e) => {
    if (mentionQuery !== null && mentionSuggestions.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIndex(i => Math.min(i + 1, mentionSuggestions.length - 1)); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIndex(i => Math.max(i - 1, 0)); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insertMention(mentionSuggestions[mentionIndex].name); return; }
      if (e.key === 'Escape') { setMentionQuery(null); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const sendMessage = () => {
    const content = text.trim();
    if (!content || !socketRef.current) return;
    const mentions = [];
    const mentionRegex = /@(\w[\w\s]*?)(?=\s|$)/g;
    let m;
    while ((m = mentionRegex.exec(content)) !== null) {
      const found = allStaff.find(s => s.name.toLowerCase() === m[1].toLowerCase());
      if (found) mentions.push(found.email);
    }
    if (activeConv === 'group') {
      socketRef.current.emit('send_message', { content, mentions: mentions.length ? JSON.stringify(mentions) : null });
    } else {
      socketRef.current.emit('send_direct', { to: activeConv, content });
    }
    setText('');
    setMentionQuery(null);
    socketRef.current.emit('stop_typing', activeConv === 'group' ? {} : { to: activeConv });
  };

  const sendFile = async (file, recipientId) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (recipientId) fd.append('recipient_id', recipientId);
      await axios.post(`${API_BASE}/api/chat/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
    } catch { /* ignore */ }
    setUploading(false);
  };

  const handleFileInput = (e) => {
    const file = e.target.files?.[0];
    if (file) sendFile(file, activeConv === 'group' ? null : activeConv);
    e.target.value = '';
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunks.current = [];
      const mr = new MediaRecorder(stream);
      mediaRecRef.current = mr;
      mr.ondataavailable = e => audioChunks.current.push(e.data);
      mr.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(audioChunks.current, { type: 'audio/webm' });
        const file = new File([blob], `voice-${Date.now()}.webm`, { type: 'audio/webm' });
        sendFile(file, activeConv === 'group' ? null : activeConv);
      };
      mr.start();
      setRecording(true);
    } catch { /* mic denied */ }
  };

  const stopRecording = () => { mediaRecRef.current?.stop(); setRecording(false); };

  // ── Render helpers ──
  const convMessages = messages[activeConv] || [];

  // Group by date for separators
  const grouped = [];
  let lastDate = null;
  convMessages.forEach(msg => {
    const dl = fmtDate(msg.created_at);
    if (dl !== lastDate) { grouped.push({ type: 'date', label: dl }); lastDate = dl; }
    grouped.push({ type: 'msg', msg });
  });

  if (!user || !token) return null;

  const activeStaff = activeConv !== 'group' ? allStaff.find(s => s.email === activeConv) : null;
  const activeIsOnline = activeConv !== 'group' && onlineUsers.some(u => u.email === activeConv);

  return ReactDOM.createPortal(
    <>
      {/* ── Floating button ── */}
      <div className="fixed bottom-6 right-6 z-[9999]">
        {/* Persistent slow pulse ring when there are unread messages */}
        {totalUnread > 0 && (
          <span className="absolute inset-0 rounded-full bg-yellow-400 animate-ping opacity-60" style={{ animationDuration: '1.4s' }} />
        )}
        {/* One-shot sharp ring on each new message arrival */}
        {btnPing && (
          <span className="absolute inset-0 rounded-full bg-yellow-300 animate-ping opacity-80" style={{ animationDuration: '0.6s' }} />
        )}
        <button
          onClick={() => { setOpen(o => !o); setBtnPing(false); if (!open) setUnread(prev => ({ ...prev, [activeConv]: 0 })); }}
          className="relative w-14 h-14 rounded-full bg-yellow-400 shadow-2xl flex items-center justify-center hover:bg-yellow-300 transition-all"
          title="Staff Chat"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.77 9.77 0 01-4-.84L3 20l1.09-3.27A7.96 7.96 0 013 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          {totalUnread > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 shadow">
              {totalUnread > 99 ? '99+' : totalUnread}
            </span>
          )}
        </button>
      </div>

      {/* ── Chat panel ── */}
      {open && (
        <div
          className="fixed z-[9998] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
          style={{ bottom: 88, right: 24, width: 640, height: 560, maxWidth: 'calc(100vw - 2rem)', maxHeight: 'calc(100vh - 6rem)' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-black shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full bg-green-400 animate-pulse" />
              <span className="text-yellow-400 font-bold text-sm">Staff Chat</span>
              <span className="text-gray-500 text-xs ml-1">{onlineUsers.length} online</span>
            </div>
            <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-white text-xl leading-none">&times;</button>
          </div>

          {/* Body */}
          <div className="flex flex-1 overflow-hidden">

            {/* ── Left sidebar ── */}
            <div className="w-52 shrink-0 border-r border-gray-100 flex flex-col overflow-hidden bg-gray-50">
              {/* Group Chat entry */}
              <button
                onClick={() => switchConv('group')}
                className={`flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-white transition border-b border-gray-100 ${activeConv === 'group' ? 'bg-white border-l-2 border-l-yellow-400' : ''}`}
              >
                <div className="w-9 h-9 shrink-0 rounded-full bg-black flex items-center justify-center text-yellow-400 font-bold text-sm">G</div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-gray-900">Group Chat</p>
                  <p className="text-[10px] text-gray-500 truncate">All staff · {allStaff.length} members</p>
                </div>
                {(unread.group || 0) > 0 && (
                  <span className="shrink-0 bg-yellow-400 text-black text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                    {unread.group}
                  </span>
                )}
              </button>

              {/* Staff list */}
              <div className="px-3 pt-2 pb-1">
                <p className="text-[9px] font-black uppercase tracking-widest text-gray-400">Direct Messages</p>
              </div>
              <div className="flex-1 overflow-y-auto">
                {allStaff
                  .filter(s => s.email !== user?.email)
                  .map(s => {
                    const isOnline = onlineUsers.some(u => u.email === s.email);
                    const dm = unread[s.email] || 0;
                    return (
                      <button
                        key={s.email}
                        onClick={() => switchConv(s.email)}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-white transition ${activeConv === s.email ? 'bg-white border-l-2 border-l-yellow-400' : ''}`}
                      >
                        <div className="relative shrink-0">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[11px] font-bold" style={{ background: getColor(s.name) }}>
                            {getInitials(s.name)}
                          </div>
                          <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-gray-50 ${isOnline ? 'bg-green-400' : 'bg-gray-300'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] font-bold text-gray-800 truncate">{s.name}</p>
                          <p className="text-[10px] text-gray-500 truncate">{s.role}</p>
                        </div>
                        {dm > 0 && (
                          <span className="shrink-0 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">{dm}</span>
                        )}
                      </button>
                    );
                  })}
              </div>
            </div>

            {/* ── Right: chat area ── */}
            <div className="flex-1 flex flex-col overflow-hidden">

              {/* Conversation header */}
              <div className="px-4 py-2 border-b border-gray-100 flex items-center gap-2.5 shrink-0 bg-white">
                {activeConv === 'group' ? (
                  <>
                    <div className="w-8 h-8 rounded-full bg-black flex items-center justify-center text-yellow-400 font-bold text-sm">G</div>
                    <div>
                      <p className="text-xs font-bold text-gray-900">Group Chat</p>
                      <p className="text-[10px] text-green-500 font-medium">{onlineUsers.length} staff online</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="relative">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[11px] font-bold" style={{ background: getColor(activeStaff?.name) }}>
                        {getInitials(activeStaff?.name)}
                      </div>
                      <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${activeIsOnline ? 'bg-green-400' : 'bg-gray-300'}`} />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-900">{activeStaff?.name || activeConv}</p>
                      <p className={`text-[10px] font-medium ${activeIsOnline ? 'text-green-500' : 'text-gray-400'}`}>
                        {activeIsOnline ? 'Online' : 'Offline'} · {activeStaff?.role}
                      </p>
                    </div>
                  </>
                )}
                {/* Clear chat button */}
                {(messages[activeConv] || []).length > 0 && (
                  <button
                    onClick={handleClearChat}
                    className="ml-auto text-[10px] font-semibold text-red-400 hover:text-red-600 transition px-2 py-1 rounded-lg hover:bg-red-50"
                    title="Clear chat"
                  >
                    🗑 Clear
                  </button>
                )}
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-3 py-3 space-y-1 bg-gray-50">
                {grouped.length === 0 && (
                  <p className="text-center text-xs text-gray-400 mt-8">
                    {activeConv === 'group' ? 'Say hello to the team!' : `Start a conversation with ${activeStaff?.name || 'this staff'}`}
                  </p>
                )}
                {grouped.map((item, i) => {
                  if (item.type === 'date') return (
                    <div key={i} className="flex items-center my-2">
                      <div className="flex-1 h-px bg-gray-200" />
                      <span className="mx-2 text-[10px] text-gray-400 font-medium">{item.label}</span>
                      <div className="flex-1 h-px bg-gray-200" />
                    </div>
                  );
                  const { msg } = item;
                  const isMe = msg.sender_id === user?.email;
                  return (
                    <div key={msg.id} className={`flex items-end gap-1.5 ${isMe ? 'flex-row-reverse' : 'flex-row'}`}>
                      {!isMe && (
                        <div className="w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-white text-[10px] font-bold mb-0.5" style={{ background: getColor(msg.sender_name) }}>
                          {getInitials(msg.sender_name)}
                        </div>
                      )}
                      <div className={`max-w-[68%] flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                        {!isMe && activeConv === 'group' && (
                          <span className="text-[10px] text-gray-500 mb-0.5 ml-1 font-medium">{msg.sender_name} · {msg.sender_role}</span>
                        )}
                        <div className={`rounded-2xl px-3 py-2 text-sm shadow-sm ${isMe ? 'bg-yellow-400 text-black rounded-br-none' : 'bg-white text-gray-800 border border-gray-200 rounded-bl-none'}`}>
                          {msg.message_type === 'text' && (
                            <span className="whitespace-pre-wrap break-words leading-snug">
                              {/* Highlight @mentions */}
                              {msg.content.split(/(@\w[\w\s]*?)(?=\s|$)/g).map((part, pi) =>
                                part.startsWith('@')
                                  ? <span key={pi} className={`font-bold ${isMe ? 'text-black' : 'text-blue-600'}`}>{part}</span>
                                  : part
                              )}
                            </span>
                          )}
                          {msg.message_type === 'image' && (
                            <a href={`${API_BASE}${msg.file_url}`} target="_blank" rel="noreferrer">
                              <img src={`${API_BASE}${msg.file_url}`} alt="img" className="max-w-full max-h-40 rounded-xl object-cover cursor-pointer" />
                            </a>
                          )}
                          {msg.message_type === 'voice' && (
                            <audio controls src={`${API_BASE}${msg.file_url}`} className="h-8 max-w-[180px]" />
                          )}
                        </div>
                        <span className="text-[10px] text-gray-400 mt-0.5 mx-1">{fmtTime(msg.created_at)}</span>
                      </div>
                    </div>
                  );
                })}
                {typingLabel && (
                  <div className="flex items-center gap-1.5 px-1 text-[11px] text-gray-400 italic">
                    <span className="flex gap-0.5">
                      {[0, 150, 300].map(d => <span key={d} className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: `${d}ms` }} />)}
                    </span>
                    {typingLabel}
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* @mention autocomplete */}
              {mentionQuery !== null && mentionSuggestions.length > 0 && (
                <div className="border-t border-gray-100 bg-white shadow-md max-h-32 overflow-y-auto shrink-0">
                  {mentionSuggestions.map((s, idx) => (
                    <button
                      key={s.email}
                      onClick={() => insertMention(s.name)}
                      className={`w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-50 transition ${idx === mentionIndex ? 'bg-yellow-50' : ''}`}
                    >
                      <div className="w-6 h-6 shrink-0 rounded-full flex items-center justify-center text-white text-[9px] font-bold" style={{ background: getColor(s.name) }}>
                        {getInitials(s.name)}
                      </div>
                      <span className="text-xs font-bold text-gray-800">{s.name}</span>
                      <span className="text-[10px] text-gray-500">{s.role}</span>
                      {onlineUsers.some(u => u.email === s.email) && (
                        <span className="ml-auto text-[9px] text-green-500 font-bold">● Online</span>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {/* Input bar */}
              <div className="px-3 py-2 border-t border-gray-200 bg-white shrink-0">
                {uploading && <p className="text-[10px] text-yellow-600 text-center mb-1">Uploading...</p>}
                <div className="flex items-end gap-2">
                  {/* Image attach */}
                  <button onClick={() => fileInputRef.current?.click()} className="shrink-0 text-gray-400 hover:text-yellow-500 transition p-1" title="Attach image">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </button>
                  <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileInput} />

                  {/* Voice record — hold to record */}
                  <button
                    onMouseDown={startRecording} onMouseUp={stopRecording}
                    onTouchStart={startRecording} onTouchEnd={stopRecording}
                    className={`shrink-0 p-1 transition ${recording ? 'text-red-500 animate-pulse' : 'text-gray-400 hover:text-yellow-500'}`}
                    title="Hold to record voice"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    </svg>
                  </button>

                  {/* Text input */}
                  <textarea
                    ref={inputRef}
                    value={text}
                    onChange={handleTyping}
                    onKeyDown={handleKeyDown}
                    placeholder={activeConv === 'group' ? 'Message all staff... (type @ to mention)' : `Message ${activeStaff?.name || ''}...`}
                    rows={1}
                    className="flex-1 resize-none rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-yellow-400 bg-gray-50 max-h-20"
                    style={{ lineHeight: '1.4' }}
                  />

                  {/* Send */}
                  <button
                    onClick={sendMessage}
                    disabled={!text.trim()}
                    className="shrink-0 w-9 h-9 rounded-full bg-yellow-400 flex items-center justify-center hover:bg-yellow-300 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-black" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
}
